/* eslint-disable react/prop-types */
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import Checkbox from '../../../componentLibrary/Checkbox';
import Button from '../../../componentLibrary/Button';
import BlockPadding from '../../../componentLibrary/BlockPadding';
import BlockColorPicker from '../../../componentLibrary/BlockColorPicker/BlockColorPicker';
import ColumnWidthControls from './ColumnWidthControls';
import * as canvasActions from '../../../actions/canvasBlocks';
import * as selectedBlocksActions from '../../../actions/selectedBlocks';
import * as Styled from './styles';
import HorizontalRule from '../../../componentLibrary/HorizontalRule';
import { sessionType } from '../../../types';
import { DOWN, UP } from '../../../constants/misc';
import Tooltip from '../../../componentLibrary/Tooltip';
import handleKeyPress from '../../../common/handleKeyPress';
import { setApplicationFocus } from '../../../actions/browser';
import APPLICATION_FOCUS from '../../../constants/applicationFocus';

class BlockSettings extends PureComponent {
    state = {
        canLockBlocks: false,
        singleBlockContainsRemoteSelection: false,
        remoteUserName: null,
        remoteSelectedBlocks: [],
        columnSizing: [],
        displayBlockColorPicker: false,
        displayWrapperColorPicker: false,
        blockID: null,
        canvasIsEmpty: true,
        validSelectedBlockIDs: [],
        canvasBlockIDs: null,
        allBlocksAreSelected: false,
        structureLocked: false,
        contentValues: {
            backgroundColor: '#5a5858',
            wrapperBackgroundColor: '#5a5858',
            paddingTop: 20,
            paddingBottom: 20,
        },
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { dispatch, canvasBlocks, selectedBlocks, remoteSelection, applicationFocus } = nextProps;
        const remoteSelectedBlocks = Object.values(remoteSelection).map((el) => el.blockIndex);
        const singleBlockContainsRemoteSelection =
            selectedBlocks.length === 1 && remoteSelectedBlocks.includes(selectedBlocks[0]);
        const canvasIsEmpty = canvasBlocks.length === 0;
        const selectedBlocksExist = canvasBlocks.length > 0 && selectedBlocks.length > 0;
        const allBlocksAreSelected = selectedBlocksExist && canvasBlocks.length === selectedBlocks.length;
        const canvasBlockIDs = canvasBlocks.map((elm) => elm.index);

        // give first element focus and reset
        if (applicationFocus === APPLICATION_FOCUS.RIGHT_PANEL) {
            dispatch(setApplicationFocus(APPLICATION_FOCUS.NONE));
        }

        if (selectedBlocks.length === 0 || canvasBlocks.length === 0) {
            return {
                ...prevState,
                canvasIsEmpty,
                canvasBlockIDs,
                validSelectedBlockIDs: [],
                allBlocksAreSelected,
                displayBlockColorPicker: false,
                displayWrapperColorPicker: false,
            };
        }

        const validSelectedBlockIDs = canvasBlocks
            .map((block) => {
                const blockIndex = block.index;
                const isValid = selectedBlocks.includes(blockIndex);
                return isValid ? blockIndex : undefined;
            })
            .filter((elm) => elm !== undefined);

        if (validSelectedBlockIDs.length === 0) {
            return {
                ...prevState,
                canvasIsEmpty,
                canvasBlockIDs,
                validSelectedBlockIDs,
                allBlocksAreSelected,
                displayBlockColorPicker: false,
                displayWrapperColorPicker: false,
                columnCount: null,
            };
        }

        const blockID = validSelectedBlockIDs[0];
        const block = canvasBlocks.find((foundBlock) => foundBlock.index === blockID);
        if (!block) {
            return { ...prevState };
        }

        const { firstName, lastName } = singleBlockContainsRemoteSelection && Object.values(remoteSelection)[0].remote;
        const remoteUserName = singleBlockContainsRemoteSelection ? `${firstName} ${lastName}` : null;

        const backgroundColor = _get(block, 'content.backgroundColor', '#5a5858');
        const wrapperBackgroundColor = _get(block, 'content.wrapperBackgroundColor', '#5a5858');
        const { paddingTop, paddingBottom, structureLocked } = block.content;
        const columnSizing = block.content.columns.reduce((acc, column) => {
            acc.push(Number(column.size) * 100);
            return acc;
        }, []);
        const canLockBlocks = canvasIsEmpty;
        const contentValues = { backgroundColor, wrapperBackgroundColor, paddingTop, paddingBottom };

        return {
            ...prevState,
            blockID,
            singleBlockContainsRemoteSelection,
            remoteUserName,
            remoteSelectedBlocks,
            structureLocked,
            canvasIsEmpty,
            canvasBlockIDs,
            validSelectedBlockIDs,
            allBlocksAreSelected,
            canLockBlocks,
            contentValues,
            columnSizing,
        };
    }

    _updateBlockProperty(keys, value) {
        const { contentValues, validSelectedBlockIDs } = this.state;
        const { dispatch } = this.props;
        const newContentValues = { ...contentValues, [keys]: value };
        dispatch(canvasActions.updateBlocksProperties({ blockIDs: validSelectedBlockIDs, property: { keys, value } }));
        this.setState((prevState) => ({
            ...prevState,
            contentValues: newContentValues,
        }));
    }

    render() {
        const {
            displayBlockColorPicker,
            displayWrapperColorPicker,
            canvasIsEmpty,
            canvasBlockIDs,
            canLockBlocks,
            validSelectedBlockIDs,
            allBlocksAreSelected,
            contentValues,
            structureLocked,
            remoteSelectedBlocks,
            remoteUserName,
            singleBlockContainsRemoteSelection,
            columnSizing,
            blockID,
        } = this.state;
        const {
            dispatch,
            canvasBlocks,
            isShiftKeyHeld,
            applicationFocus,
            session: { user, integration },
        } = this.props;
        const {
            customization: { colors },
            features,
            isAdminOrParentAdmin,
        } = user;
        const { backgroundColor, wrapperBackgroundColor, paddingTop, paddingBottom } = contentValues;
        const selectedBlocksExist = validSelectedBlockIDs.length > 0;
        const selectedBlocks = canvasBlocks.filter((block) => validSelectedBlockIDs.includes(block.index));
        const brandColors = JSON.parse(colors).filter((item) => Object.prototype.hasOwnProperty.call(item, 'color'));
        const hasFullStylingAccess =
            features.EMAIL_DESIGNER_STYLING_ACCESS === undefined ? true : features.EMAIL_DESIGNER_STYLING_ACCESS;
        const hasCustomizationFeature = features.EMAIL_DESIGNER_CUSTOMIZATION;
        const hasCustomizationAccess = isAdminOrParentAdmin && (integration === 'LOCALHOST' || integration === 'APP');

        return (
            <Styled.BlockSettingsWrapper>
                <Styled.BlockSettingDiv canvasIsEmpty={canvasIsEmpty}>
                    <HorizontalRule styling={{ marginBottom: 5 }} />
                    <Styled.ControlsWrapper>
                        <Styled.SelectAllWrapper>
                            <div
                                data-test-id="select-all-blocks-container"
                                style={{ height: 20, display: 'flex', alignItems: 'center' }}
                            >
                                <Checkbox
                                    width={28}
                                    disabled={canvasIsEmpty}
                                    // eslint-disable-next-line no-nested-ternary
                                    color={canvasIsEmpty ? 'lightgray' : !allBlocksAreSelected ? '#979799' : '#0277BD'}
                                    icon={allBlocksAreSelected ? 'check_box' : 'check_box_outline_blank'}
                                    isChecked={allBlocksAreSelected}
                                    isFocused={applicationFocus === APPLICATION_FOCUS.RIGHT_PANEL}
                                    labelId="select-all-blocks-label"
                                    onClick={() =>
                                        allBlocksAreSelected
                                            ? dispatch(selectedBlocksActions.removeAllSelectedBlocks())
                                            : dispatch(
                                                  selectedBlocksActions.selectAllBlocks({ blockIDs: canvasBlockIDs })
                                              )
                                    }
                                    onKeyDown={(e) =>
                                        handleKeyPress(e, () =>
                                            allBlocksAreSelected
                                                ? dispatch(selectedBlocksActions.removeAllSelectedBlocks())
                                                : dispatch(
                                                      selectedBlocksActions.selectAllBlocks({
                                                          blockIDs: canvasBlockIDs,
                                                      })
                                                  )
                                        )
                                    }
                                />
                                <Styled.Label id="select-all-blocks-label" margin="0 0 2px 5px">
                                    Select all blocks
                                </Styled.Label>
                            </div>
                        </Styled.SelectAllWrapper>
                        <Styled.ButtonsWrapper>
                            <Tooltip
                                id="block-settings-lock"
                                disableTooltip={!singleBlockContainsRemoteSelection}
                                text={`Lock is unavailable while ${remoteUserName} is editing an element in this block.`}
                            >
                                <Button
                                    type="simple"
                                    width={25}
                                    iconSize="14"
                                    padding={0}
                                    iconPadding={6}
                                    label={structureLocked ? 'Unlock' : 'Lock'}
                                    icon={structureLocked ? 'lock' : 'lock-open-alt'}
                                    disabled={
                                        canvasIsEmpty ||
                                        !selectedBlocksExist ||
                                        canLockBlocks ||
                                        singleBlockContainsRemoteSelection
                                    }
                                    marginBottom="0px"
                                    color="#424242"
                                    onClick={() =>
                                        dispatch(
                                            canvasActions.lockBlocks({
                                                blockIDs: validSelectedBlockIDs.filter(
                                                    (id) => !remoteSelectedBlocks.includes(id)
                                                ),
                                            })
                                        )
                                    }
                                    data-test-id="lock-blocks-btn"
                                />
                            </Tooltip>
                            <Button
                                type="simple"
                                width={25}
                                iconSize="14"
                                padding={0}
                                iconPadding={6}
                                label={isShiftKeyHeld ? 'Move to Top' : 'Move Up'}
                                icon={isShiftKeyHeld ? ['far', 'arrow-to-top'] : ['far', 'arrow-up']}
                                disabled={
                                    canvasIsEmpty ||
                                    !selectedBlocksExist ||
                                    structureLocked ||
                                    singleBlockContainsRemoteSelection
                                }
                                marginBottom="0px"
                                color="#424242"
                                onClick={() => {
                                    dispatch(
                                        canvasActions.moveBlocks({
                                            blocks: selectedBlocks,
                                            direction: UP,
                                            isToEdge: isShiftKeyHeld,
                                        })
                                    );
                                }}
                                data-test-id="move-up-blocks-btn"
                            />
                            <Button
                                type="simple"
                                width={25}
                                iconSize="14"
                                padding={0}
                                iconPadding={6}
                                label={isShiftKeyHeld ? 'Move to Bottom' : 'Move Down'}
                                icon={isShiftKeyHeld ? ['far', 'arrow-to-bottom'] : ['far', 'arrow-down']}
                                disabled={
                                    canvasIsEmpty ||
                                    !selectedBlocksExist ||
                                    structureLocked ||
                                    singleBlockContainsRemoteSelection
                                }
                                marginBottom="0px"
                                color="#424242"
                                onClick={() => {
                                    dispatch(
                                        canvasActions.moveBlocks({
                                            blocks: selectedBlocks,
                                            direction: DOWN,
                                            isToEdge: isShiftKeyHeld,
                                        })
                                    );
                                }}
                                data-test-id="move-down-blocks-btn"
                            />
                            <Button
                                type="simple"
                                width={25}
                                iconSize="14"
                                padding={0}
                                iconPadding={6}
                                label="Duplicate"
                                icon={['far', 'copy']}
                                disabled={
                                    canvasIsEmpty ||
                                    !selectedBlocksExist ||
                                    structureLocked ||
                                    singleBlockContainsRemoteSelection
                                }
                                marginBottom="0px"
                                color="#424242"
                                onClick={() => {
                                    dispatch(canvasActions.duplicateBlocks({ blocks: selectedBlocks }));
                                    setTimeout(() => {
                                        if (selectedBlocks.length <= 1) {
                                            return;
                                        }
                                        // Need to access props directly from the setTimeout to prevent stale canvasBlocks state
                                        const { canvasBlocks: updatedCanvasBlocks } = this.props;
                                        const topDuplicatedBlock =
                                            updatedCanvasBlocks[updatedCanvasBlocks.length - selectedBlocks.length];
                                        const newBlock = document.getElementById(topDuplicatedBlock.index);
                                        newBlock && newBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }, 250);
                                }}
                                data-test-id="duplicate-blocks-btn"
                            />
                            <Tooltip
                                id="block-settings-delete"
                                disableTooltip={!singleBlockContainsRemoteSelection}
                                text={`Delete is unavailable while ${remoteUserName} is editing an element in this block.`}
                            >
                                <Button
                                    type="simple"
                                    width={25}
                                    iconPadding={6}
                                    label="Delete"
                                    icon="trash"
                                    iconSize="14"
                                    disabled={
                                        canvasIsEmpty ||
                                        !selectedBlocksExist ||
                                        structureLocked ||
                                        singleBlockContainsRemoteSelection
                                    }
                                    marginBottom="0px"
                                    color="#424242"
                                    onClick={() =>
                                        dispatch(
                                            canvasActions.deleteBlocks({
                                                blockIDs: validSelectedBlockIDs.filter(
                                                    (id) => !remoteSelectedBlocks.includes(id)
                                                ),
                                            })
                                        )
                                    }
                                    data-test-id="delete-blocks-btn"
                                />
                            </Tooltip>
                        </Styled.ButtonsWrapper>
                    </Styled.ControlsWrapper>
                    <HorizontalRule styling={{ marginBottom: 4, marginTop: 12 }} />
                    {selectedBlocksExist && (
                        <Styled.InnerSettingsDiv structureLocked={structureLocked}>
                            <div>
                                <Styled.Label>
                                    <span>Outer background color</span>
                                </Styled.Label>
                                <BlockColorPicker
                                    type="inner"
                                    shouldDisplayColorPicker={displayWrapperColorPicker}
                                    color={wrapperBackgroundColor}
                                    presetColors={brandColors}
                                    hasFullStylingAccess={hasFullStylingAccess}
                                    hasCustomizationFeature={hasCustomizationFeature}
                                    hasCustomizationAccess={hasCustomizationAccess}
                                    onClick={() =>
                                        this.setState({ displayWrapperColorPicker: !displayWrapperColorPicker })
                                    }
                                    onChangeComplete={(color) => {
                                        const { hex } = color;
                                        this._updateBlockProperty(['wrapperBackgroundColor'], hex);
                                    }}
                                    onClose={() => this.setState({ displayWrapperColorPicker: false })}
                                />
                                <Styled.Label>
                                    <span>Inner background color</span>
                                </Styled.Label>
                                <BlockColorPicker
                                    type="outer"
                                    shouldDisplayColorPicker={displayBlockColorPicker}
                                    color={backgroundColor}
                                    presetColors={brandColors}
                                    hasFullStylingAccess={hasFullStylingAccess}
                                    hasCustomizationFeature={hasCustomizationFeature}
                                    hasCustomizationAccess={hasCustomizationAccess}
                                    onClick={() => this.setState({ displayBlockColorPicker: !displayBlockColorPicker })}
                                    onChangeComplete={(color) => {
                                        const { hex } = color;
                                        this._updateBlockProperty(['backgroundColor'], hex);
                                    }}
                                    onClose={() => this.setState({ displayBlockColorPicker: false })}
                                />
                            </div>
                            <div style={{ marginTop: 16 }}>
                                <Styled.Label>
                                    <span>BLOCK SPACING</span>
                                </Styled.Label>
                                <BlockPadding
                                    paddingTop={paddingTop}
                                    paddingBottom={paddingBottom}
                                    disabled={canvasIsEmpty || !selectedBlocksExist}
                                    onChange={(event, padName) => {
                                        let paddingTopValue;
                                        let paddingBottomValue;
                                        if (padName === 'linked') {
                                            paddingTopValue = +event.target.value;
                                            paddingBottomValue = +event.target.value;
                                            this._updateBlockProperty(['paddingTop', 'paddingBottom'], paddingTopValue);
                                            return;
                                        }
                                        paddingTopValue = padName === 'paddingTop' ? +event.target.value : paddingTop;
                                        paddingBottomValue =
                                            padName === 'paddingBottom' ? +event.target.value : paddingBottom;
                                        if (paddingTopValue !== paddingTop) {
                                            this._updateBlockProperty(['paddingTop'], paddingTopValue);
                                        }
                                        if (paddingBottomValue !== paddingBottom) {
                                            this._updateBlockProperty(['paddingBottom'], paddingBottomValue);
                                        }
                                    }}
                                />
                                {validSelectedBlockIDs.length === 1 && columnSizing.length > 1 && (
                                    <Styled.ColumnWidthWrapper>
                                        <Styled.Label>
                                            <span>COLUMN WIDTHS</span>
                                        </Styled.Label>
                                        <ColumnWidthControls
                                            disabled={structureLocked}
                                            dispatch={dispatch}
                                            blockID={blockID}
                                            columnSizing={columnSizing}
                                        />
                                    </Styled.ColumnWidthWrapper>
                                )}
                            </div>
                        </Styled.InnerSettingsDiv>
                    )}
                </Styled.BlockSettingDiv>
            </Styled.BlockSettingsWrapper>
        );
    }
}

const mapStateToProps = (state) => ({
    canvasBlocks: state.canvasBlocks.present,
    remoteSelection: state.selectedElements.remote,
    selectedBlocks: state.selectedBlocks,
    session: state.session,
    isShiftKeyHeld: state.browser.modifierKeyPressed,
    applicationFocus: state.browser.applicationFocus,
});

BlockSettings.propTypes = {
    dispatch: PropTypes.func.isRequired,
    session: sessionType.isRequired,
    isShiftKeyHeld: PropTypes.bool.isRequired,
    applicationFocus: PropTypes.string.isRequired,
};

export default connect(mapStateToProps)(BlockSettings);
